import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    useTheme,
    Chip,
    Button,
    InputBase,
    IconButton,
    Autocomplete,
    TextField,
    Checkbox,
    FormControlLabel,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Tooltip,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import Axios from 'axios';
import { tokens } from '../../theme';
import { getAuthHeader, getAuthToken } from '../../utils/authToken';
import Header from '../../components/Header';
import { SocketContext } from '../../socket';
import { scaleFromKey, formatScaled } from '../../classrooms/pages/tests/v2/scoreScale';

function flattenFolders(items, depth = 0) {
    const result = [];
    for (const item of items) {
        const indent = depth > 0 ? '  '.repeat(depth - 1) + '└ ' : '';
        result.push({ id: item.id, name: item.name, label: indent + item.name, depth, hasChildren: item.children?.length > 0 });
        if (item.children?.length > 0) {
            result.push(...flattenFolders(item.children, depth + 1));
        }
    }
    return result;
}

const LS_KEY = 'testResults_filters';
const loadSavedFilters = () => {
    try { return JSON.parse(localStorage.getItem(LS_KEY)) || {}; } catch { return {}; }
};

const TestResults = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const saved = useRef((() => {
        const base = loadSavedFilters();
        // Allow URL params to override persisted filters on landing — e.g. when
        // an email link sends the admin here with a pre-filled course filter
        // (?courseIds=1,2,3&clearOthers=1). Stripped from the URL after parsing
        // so a refresh doesn't re-apply them.
        try {
            const params = new URLSearchParams(window.location.search);
            const courseIdsParam = params.get('courseIds');
            const semesterIdsParam = params.get('semesterIds');
            const searchQueryParam = params.get('searchQuery');
            const testCategoryParam = params.get('testCategory');
            const clearOthers = params.get('clearOthers') === '1';
            if (courseIdsParam) {
                base.courseIds = courseIdsParam.split(',').map(s => parseInt(s, 10)).filter(Number.isFinite);
            }
            if (semesterIdsParam) {
                base.semesterIds = semesterIdsParam.split(',').map(s => parseInt(s, 10)).filter(Number.isFinite);
                base.semesterIdsFromUrl = true;
            }
            if (searchQueryParam) {
                base.searchQuery = searchQueryParam;
            }
            if (testCategoryParam && ['all', 'regular', 'midterm', 'final'].includes(testCategoryParam)) {
                base.testCategoryFilter = testCategoryParam;
            }
            if (clearOthers) {
                base.categoryId = null;
                if (!searchQueryParam) base.searchQuery = '';
                if (!testCategoryParam) base.testCategoryFilter = 'all';
                if (!semesterIdsParam) {
                    base.semesterIds = [];
                    base.semesterId = null;
                }
            }
            if (courseIdsParam || semesterIdsParam || searchQueryParam || testCategoryParam || clearOthers) {
                params.delete('courseIds');
                params.delete('semesterIds');
                params.delete('searchQuery');
                params.delete('testCategory');
                params.delete('clearOthers');
                const cleanQuery = params.toString();
                const cleanUrl = window.location.pathname + (cleanQuery ? `?${cleanQuery}` : '');
                window.history.replaceState({}, '', cleanUrl);
            }
        } catch (_) { /* URL parsing is best-effort */ }
        return base;
    })());

    // Filter states
    const [semesters, setSemesters] = useState([]);
    const [categories, setCategories] = useState([]); // flat list with depth
    // Semester filter — multi-select. Empty array = no filter ("all semesters").
    const [selectedSemesters, setSelectedSemesters] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    // Course filter — multi-select. Empty array = no filter ("all courses").
    const [selectedCourses, setSelectedCourses] = useState([]);
    const [searchQuery, setSearchQuery] = useState(saved.current.searchQuery || '');
    // Bumped by handleResetFilters to force-remount the (uncontrolled) search
    // input so its defaultValue picks up the cleared searchQuery.
    const [searchInputKey, setSearchInputKey] = useState(0);
    const [testCategoryFilter, setTestCategoryFilter] = useState(saved.current.testCategoryFilter || 'all');
    // Layout mode: 'per-course' (default — one table per course, no "Not assigned" cells)
    // or 'single' (legacy single big grid). Persisted alongside other filters.
    const [viewMode, setViewMode] = useState(saved.current.viewMode || 'per-course');

    // Data states
    const [loading, setLoading] = useState(false);
    const [tests, setTests] = useState([]);
    const [students, setStudents] = useState([]);
    const [courses, setCourses] = useState([]);
    // Map "{courseId}_{testId}" → assignment-specific title from v2_course_tests.title.
    // Used by per-course view to show "Full Test 1" instead of the canonical PDF title.
    const [assignedTitles, setAssignedTitles] = useState({});
    // Map "{courseId}_{testId}" → ISO due_date from v2_course_tests.due_date.
    const [assignedDueDates, setAssignedDueDates] = useState({});
    // Map "{courseId}_{testId}" → v2_course_tests.id, used to deep-link the
    // per-course grading dashboard from the assignment-title click.
    const [assignedCourseTestIds, setAssignedCourseTestIds] = useState({});

    // Selection + expand state
    const [selectedCells, setSelectedCells] = useState(new Set()); // Set of "studentId:courseId:testId"
    const [expanded, setExpanded] = useState(saved.current.expanded || false); // show AI feedback + overrides

    // Feedback editing: key = "studentId:courseId:category", value = { kr, en }
    const [editingFeedback, setEditingFeedback] = useState({});
    const [savingFeedback, setSavingFeedback] = useState(new Set());

    // Per-cell regeneration preview: key = feedbackKey, value = { kr, en, loading }
    const [regenPreview, setRegenPreview] = useState({});

    // Publish dialog
    const [publishDialog, setPublishDialog] = useState({ open: false, publishDate: '', items: [] });

    // Score override dialog
    // step 1: enter mcq/frq → compute
    // step 2: review total/composite/ap (ap editable) → save
    const [overrideDialog, setOverrideDialog] = useState({ open: false, step: 1 });

    // Bulk generation loading: Set of feedbackKeys currently generating
    const [generatingCells, setGeneratingCells] = useState(new Set());

    // Confirm dialog state
    const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null });
    const [clearExamDialog, setClearExamDialog] = useState({ open: false, studentId: null, courseId: null, category: null, isPublished: false, alsoUnpublish: true });

    const socket = useContext(SocketContext);
    const refetchTimerRef = useRef(null);
    const fetchRef = useRef(null);

    // Kill outer .content scroll — table has its own
    useEffect(() => {
        const style = document.createElement('style');
        style.textContent = '.content { overflow: hidden !important; }';
        document.head.appendChild(style);
        return () => document.head.removeChild(style);
    }, []);

    // Fetch semesters and categories on mount
    useEffect(() => {
        localStorage.setItem('selectedMenu', 'Test Results');
        fetchSemesters();
        fetchCategories();
    }, []);

    // Fetch spreadsheet data when filters change
    const [ready, setReady] = useState(false);

    // Persist filters to localStorage
    useEffect(() => {
        if (!ready) return;
        localStorage.setItem(LS_KEY, JSON.stringify({
            semesterIds: selectedSemesters.map(s => s.id),
            categoryId: selectedCategory?.id || null,
            courseIds: selectedCourses.map(c => c.id),
            searchQuery,
            testCategoryFilter,
            expanded,
            viewMode,
        }));
    }, [selectedSemesters, selectedCategory, selectedCourses, searchQuery, testCategoryFilter, expanded, viewMode, ready]);

    useEffect(() => {
        if (!ready) return;
        fetchSpreadsheetData();
        // Filter changes can drop rows the user already selected, leaving
        // orphan cell-keys in selectedCells that subsequent bulk actions
        // would still try to act on. Clear selection on filter change.
        // (Silent socket-driven refetches don't run this effect, so an admin
        // mid-bulk-selection won't lose their picks to a passive event.)
        setSelectedCells(new Set());
    }, [selectedCategory, selectedSemesters, selectedCourses, searchQuery, testCategoryFilter, ready]);

    const handleResetFilters = () => {
        localStorage.removeItem(LS_KEY);
        setSelectedCategory(null);
        setSelectedCourses([]);
        setSearchQuery('');
        // Cancel any pending debounced setSearchQuery from in-flight typing.
        if (searchTimeout) clearTimeout(searchTimeout);
        // Remount the search input so it picks up the cleared defaultValue.
        setSearchInputKey(k => k + 1);
        setTestCategoryFilter('all');
        setExpanded(false);
        setSelectedCells(new Set());
        // Re-detect current semester (single-element array)
        const today = new Date();
        const currentSemester = semesters.find(sem => {
            const start = new Date(sem.start);
            const end = new Date(sem.end);
            return today >= start && today <= end;
        });
        setSelectedSemesters(currentSemester ? [currentSemester] : []);
    };

    const fetchSemesters = async () => {
        try {
            const res = await Axios.get(`${process.env.REACT_APP_URL}/api/v1/test-results/semesters`, {
                headers: { Authorization: getAuthHeader() }
            });
            setSemesters(res.data);

            // Restore saved semesters, or auto-select current.
            // Legacy `semesterId` (single id) supported for previously-stored filters.
            const savedIds = Array.isArray(saved.current.semesterIds)
                ? saved.current.semesterIds
                : (saved.current.semesterId ? [saved.current.semesterId] : []);
            if (savedIds.length > 0) {
                const matches = res.data.filter(s => savedIds.includes(s.id));
                setSelectedSemesters(matches);
            } else if (!saved.current.semesterIdsFromUrl) {
                // Only auto-default to current semester if the URL hasn't already
                // explicitly set a (possibly empty) semester list.
                const today = new Date();
                const currentSemester = res.data.find(sem => {
                    const start = new Date(sem.start);
                    const end = new Date(sem.end);
                    return today >= start && today <= end;
                });
                if (currentSemester) {
                    setSelectedSemesters([currentSemester]);
                }
            }
            setReady(true);
        } catch (err) {
            console.error('Error fetching semesters:', err);
            setReady(true);
        }
    };

    const fetchCategories = async () => {
        try {
            const res = await Axios.get(`${process.env.REACT_APP_URL}/api/v1/test-results/categories`, {
                headers: { Authorization: getAuthHeader() }
            });
            const flat = flattenFolders(res.data);
            setCategories(flat);
            // Restore saved category
            if (saved.current.categoryId) {
                const match = flat.find(c => c.id === saved.current.categoryId);
                if (match) setSelectedCategory(match);
            }
        } catch (err) {
            console.error('Error fetching categories:', err);
        }
    };

    // Targeted single-row refetch for socket-driven updates. Hits the spreadsheet
    // endpoint scoped to one student and merges the returned row into local state
    // by (studentId, courseId), keeping every other row untouched. No spinner —
    // happens silently in the background.
    const fetchRowFor = async (studentId, courseId) => {
        try {
            const params = new URLSearchParams();
            if (selectedCategory) params.append('category_id', selectedCategory.id);
            if (selectedSemesters.length > 0) params.append('semester_id', selectedSemesters.map(s => s.id).join(','));
            if (selectedCourses.length > 0) params.append('course_id', selectedCourses.map(c => c.id).join(','));
            if (testCategoryFilter && testCategoryFilter !== 'all') params.append('test_category', testCategoryFilter);
            params.append('student_id', String(studentId));
            const res = await Axios.get(`${process.env.REACT_APP_URL}/api/v1/test-results/spreadsheet?${params}`, {
                headers: { Authorization: getAuthHeader() },
            });
            const incoming = (res.data.students || []).find(s => s.studentId === studentId && s.courseId === courseId);
            if (!incoming) return;
            setStudents(prev => prev.map(s => (s.studentId === studentId && s.courseId === courseId) ? incoming : s));
        } catch (err) {
            console.error('Targeted row refetch failed, falling back to full fetch:', err);
            // Silent fallback — table already has data on screen; avoid the
            // loading flash + scroll reset.
            fetchRef.current?.({ silent: true });
        }
    };
    const fetchRowRef = useRef();
    fetchRowRef.current = fetchRowFor;

    // Bulk-action helper: patch only the unique rows touched by `items`
    // (each item has student_id + course_id). Cheaper than a full refetch
    // for small bulks, which is the typical case (admin selects ≤ ~10 cells).
    const patchRowsForItems = async (items) => {
        const unique = new Map();
        items.forEach(it => unique.set(`${it.student_id}_${it.course_id}`, [it.student_id, it.course_id]));
        await Promise.all([...unique.values()].map(([sid, cid]) => fetchRowFor(sid, cid)));
    };

    // `silent` skips the loading flag — used for socket-driven refetches so the
    // table doesn't unmount mid-update (the spreadsheet is hidden behind
    // `students.length > 0 && !loading`, so toggling loading nukes scroll
    // position). Initial mount and filter changes still pass silent=false to
    // surface the spinner during a genuine load.
    const fetchSpreadsheetData = async ({ silent = false } = {}) => {
        if (!silent) setLoading(true);
        try {
            const params = new URLSearchParams();
            if (selectedCategory) params.append('category_id', selectedCategory.id);
            if (selectedSemesters.length > 0) params.append('semester_id', selectedSemesters.map(s => s.id).join(','));
            if (selectedCourses.length > 0) params.append('course_id', selectedCourses.map(c => c.id).join(','));
            if (searchQuery) params.append('search', searchQuery);
            if (testCategoryFilter && testCategoryFilter !== 'all') params.append('test_category', testCategoryFilter);

            const res = await Axios.get(`${process.env.REACT_APP_URL}/api/v1/test-results/spreadsheet?${params}`, {
                headers: { Authorization: getAuthHeader() }
            });

            setTests((res.data.tests || []).sort((a, b) => (a.title || '').localeCompare(b.title || '', undefined, { numeric: true })));
            setStudents(res.data.students);
            setAssignedTitles(res.data.assignedTitles || {});
            setAssignedDueDates(res.data.assignedDueDates || {});
            setAssignedCourseTestIds(res.data.assignedCourseTestIds || {});
            setGeneratingCells(new Set());
            if (selectedCourses.length === 0) {
                const sorted = (res.data.courses || []).sort((a, b) => (a.code || a.title || '').localeCompare(b.code || b.title || '', undefined, { numeric: true }));
                setCourses(sorted);
                // Restore saved courses on first load (legacy single-id courseId also handled).
                const savedIds = Array.isArray(saved.current.courseIds)
                    ? saved.current.courseIds
                    : (saved.current.courseId ? [saved.current.courseId] : []);
                if (savedIds.length > 0 && selectedCourses.length === 0) {
                    const matches = sorted.filter(c => savedIds.includes(c.id));
                    if (matches.length > 0) setSelectedCourses(matches);
                    saved.current.courseIds = null;
                    saved.current.courseId = null; // only restore once
                }
            }
        } catch (err) {
            console.error('Error fetching spreadsheet data:', err);
        } finally {
            if (!silent) setLoading(false);
        }
    };
    fetchRef.current = fetchSpreadsheetData;

    // Hide test columns where no student has data
    const visibleTests = tests.filter(t => students.some(s => s.scores[t.id]));

    // Selection helpers
    const cellKey = (studentId, courseId, testId) => `${studentId}:${courseId}:${testId}`;
    const midtermFinalTests = visibleTests.filter(t => t.test_category === 'midterm' || t.test_category === 'final');

    // Column select: toggle all students for a test. Scoped to the passed
    // student list — in per-course view this is `group.students` so courses
    // don't bleed into each other's column-checkbox state. Single-view mode
    // passes the global students array since there's only one synthetic group.
    const isColumnSelected = (testId, scopedStudents = students) => {
        const eligible = scopedStudents.filter(s => s.scores[testId]);
        return eligible.length > 0 && eligible.every(s => selectedCells.has(cellKey(s.studentId, s.courseId, testId)));
    };
    const toggleColumn = (testId, scopedStudents = students) => {
        setSelectedCells(prev => {
            const next = new Set(prev);
            const allSelected = isColumnSelected(testId, scopedStudents);
            scopedStudents.filter(s => s.scores[testId]).forEach(s => {
                const key = cellKey(s.studentId, s.courseId, testId);
                if (allSelected) next.delete(key);
                else next.add(key);
            });
            return next;
        });
    };

    // Cell select: toggle one student+test
    const toggleCell = (studentId, courseId, testId) => {
        setSelectedCells(prev => {
            const next = new Set(prev);
            const key = cellKey(studentId, courseId, testId);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    // Select all: only students that actually have scores for each midterm/final test
    const allCellKeys = midtermFinalTests.flatMap(t => students.filter(s => s.scores[t.id]).map(s => cellKey(s.studentId, s.courseId, t.id)));
    const isAllSelected = allCellKeys.length > 0 && allCellKeys.every(k => selectedCells.has(k));
    const toggleSelectAll = () => {
        if (isAllSelected) setSelectedCells(new Set());
        else setSelectedCells(new Set(allCellKeys));
    };

    // Management mode: active when filtering to midterm or final specifically

    const hasMidtermFinalTests = midtermFinalTests.length > 0;

    const handleBulkGenerateFeedback = () => {
        if (selectedCells.size === 0) return;
        setConfirmDialog({
            open: true,
            title: 'Regenerate AI Feedback',
            message: `This will regenerate AI feedback for ${selectedCells.size} selected cell(s), overwriting any existing feedback. This cannot be undone.`,
            onConfirm: async () => {
                setConfirmDialog(prev => ({ ...prev, open: false }));
                // Mark all selected cells as generating
                const loadingKeys = new Set();
                const uniquePairs = new Map();
                selectedCells.forEach(key => {
                    const [studentId, courseId, testId] = key.split(':');
                    const test = tests.find(t => t.id === testId);
                    if (test) {
                        loadingKeys.add(feedbackKey(studentId, courseId, test.test_category));
                        uniquePairs.set(`${studentId}:${courseId}`, { student_id: parseInt(studentId), course_id: parseInt(courseId) });
                    }
                });
                setGeneratingCells(loadingKeys);
                setSelectedCells(new Set());

                const results = await Promise.all([...uniquePairs.values()].map(pair =>
                    Axios.post(
                        `${process.env.REACT_APP_URL}/api/v1/test-results/generate-exam-feedback`,
                        pair,
                        { headers: { Authorization: getAuthHeader() } }
                    ).then(res => ({ studentId: pair.student_id, courseId: pair.course_id, examData: res.data.examData }))
                     .catch(err => { console.error(`Failed for student ${pair.student_id}:`, err.message); return null; })
                ));

                // Patch local state with returned exam data
                setStudents(prev => prev.map(s => {
                    const match = results.find(r => r && r.studentId === s.studentId && r.courseId === s.courseId);
                    if (!match) return s;
                    return { ...s, examData: match.examData };
                }));
                setGeneratingCells(new Set());
            },
        });
    };

    const handleRegenPreview = async (studentId, courseId, category) => {
        const key = feedbackKey(studentId, courseId, category);
        setRegenPreview(prev => ({ ...prev, [key]: { kr: '', en: '', loading: true } }));
        try {
            const res = await Axios.post(
                `${process.env.REACT_APP_URL}/api/v1/test-results/preview-exam-feedback`,
                { student_id: parseInt(studentId), course_id: parseInt(courseId), category },
                { headers: { Authorization: getAuthHeader() } }
            );
            setRegenPreview(prev => ({ ...prev, [key]: { kr: res.data.kr, en: res.data.en, loading: false } }));
        } catch (err) {
            setConfirmDialog({ open: true, title: 'Error', message: err.response?.data?.error || 'Failed to regenerate preview', onConfirm: null });
            setRegenPreview(prev => { const next = { ...prev }; delete next[key]; return next; });
        }
    };

    const acceptRegenPreview = async (studentId, courseId, category) => {
        const key = feedbackKey(studentId, courseId, category);
        const preview = regenPreview[key];
        if (!preview) return;
        setSavingFeedback(prev => new Set(prev).add(key));
        try {
            await Axios.put(
                `${process.env.REACT_APP_URL}/api/v1/test-results/save-exam-feedback`,
                { student_id: parseInt(studentId), course_id: parseInt(courseId), category, ai_feedback: preview.kr, ai_feedback_en: preview.en },
                { headers: { Authorization: getAuthHeader() } }
            );
            setRegenPreview(prev => { const next = { ...prev }; delete next[key]; return next; });
            fetchRowFor(studentId, courseId);
        } catch (err) {
            setConfirmDialog({ open: true, title: 'Error', message: err.response?.data?.error || 'Failed to save', onConfirm: null });
        } finally {
            setSavingFeedback(prev => { const next = new Set(prev); next.delete(key); return next; });
        }
    };

    const dismissRegenPreview = (key) => {
        setRegenPreview(prev => { const next = { ...prev }; delete next[key]; return next; });
    };

    const handleBulkPublish = async (unpublish = false) => {
        if (selectedCells.size === 0) return;
        const items = [];
        selectedCells.forEach(key => {
            const [studentId, courseId, testId] = key.split(':');
            const test = tests.find(t => t.id === testId);
            if (test) items.push({ student_id: parseInt(studentId), course_id: parseInt(courseId), category: test.test_category });
        });

        if (unpublish) {
            // Unpublish directly, no dialog
            try {
                await Axios.post(
                    `${process.env.REACT_APP_URL}/api/v1/test-results/publish-exam-data`,
                    { items, unpublish: true },
                    { headers: { Authorization: getAuthHeader() } }
                );
                await patchRowsForItems(items);
                setSelectedCells(new Set());
            } catch (err) {
                setConfirmDialog({ open: true, title: 'Error', message: err.response?.data?.error || 'Failed to unpublish', onConfirm: null });
            }
            return;
        }

        // Safety gate — block publish if any selected cell is missing scores or
        // either AI feedback language. Lists offending rows so the admin knows
        // which to fix before retrying.
        const issues = [];
        selectedCells.forEach(key => {
            const [studentId, courseId, testId] = key.split(':');
            const student = students.find(s => s.studentId === parseInt(studentId) && s.courseId === parseInt(courseId));
            const test = tests.find(t => t.id === testId);
            if (!student || !test) return;
            const scoreData = student.scores?.[test.id];
            const examInfo = student.examData?.[test.test_category];
            const reasons = [];
            if (!scoreData || scoreData.noSubmission || scoreData.inProgress || scoreData.gradingInFlight) {
                reasons.push('no scores');
            }
            const krMissing = !examInfo?.ai_feedback || !String(examInfo.ai_feedback).trim();
            const enMissing = !examInfo?.ai_feedback_en || !String(examInfo.ai_feedback_en).trim();
            if (krMissing && enMissing) reasons.push('missing AI feedback (KR + EN)');
            else if (krMissing) reasons.push('missing AI feedback (KR)');
            else if (enMissing) reasons.push('missing AI feedback (EN)');
            if (reasons.length > 0) {
                issues.push(`${student.studentName} ${test.test_category} — ${reasons.join('; ')}`);
            }
        });
        if (issues.length > 0) {
            setConfirmDialog({
                open: true,
                title: 'Cannot publish',
                message: `${issues.length} of ${selectedCells.size} selection(s) are not ready:\n\n• ${issues.join('\n• ')}\n\nResolve these and try again.`,
                onConfirm: null,
            });
            return;
        }

        // Find existing published_at from any selected cell to prefill
        let existingDate = null;
        selectedCells.forEach(key => {
            if (existingDate) return;
            const [studentId, courseId, testId] = key.split(':');
            const student = students.find(s => s.studentId === parseInt(studentId) && s.courseId === parseInt(courseId));
            const test = tests.find(t => t.id === testId);
            if (student?.examData && test) {
                const ed = student.examData[test.test_category];
                if (ed?.published_at) existingDate = ed.published_at;
            }
        });

        const defaultDate = existingDate
            ? new Date(existingDate).toISOString().split('T')[0]
            : new Date().toISOString().split('T')[0];

        setPublishDialog({ open: true, publishDate: defaultDate, items });
    };

    const handleConfirmPublish = async () => {
        const { items, publishDate } = publishDialog;
        setPublishDialog(prev => ({ ...prev, open: false }));
        try {
            await Axios.post(
                `${process.env.REACT_APP_URL}/api/v1/test-results/publish-exam-data`,
                { items, published_at: publishDate },
                { headers: { Authorization: getAuthHeader() } }
            );
            await patchRowsForItems(items);
            setSelectedCells(new Set());
        } catch (err) {
            setConfirmDialog({ open: true, title: 'Error', message: err.response?.data?.error || 'Failed to publish', onConfirm: null });
        }
    };

    const handleComputeOverride = async () => {
        const { studentId, courseId, category, mcq, frq } = overrideDialog;
        const mcqVal = mcq === '' ? null : parseFloat(mcq);
        const frqVal = frq === '' ? null : parseFloat(frq);
        if ((mcq !== '' && (isNaN(mcqVal) || mcqVal < 0)) || (frq !== '' && (isNaN(frqVal) || frqVal < 0))) return;

        // If both blank, clear override and close
        if (mcqVal === null && frqVal === null) {
            setOverrideDialog(prev => ({ ...prev, saving: true }));
            try {
                const res = await Axios.put(
                    `${process.env.REACT_APP_URL}/api/v1/test-results/save-score-override`,
                    { student_id: parseInt(studentId), course_id: parseInt(courseId), category, mcq_score: null, frq_score: null },
                    { headers: { Authorization: getAuthHeader() } }
                );
                setStudents(prev => prev.map(s => {
                    if (s.studentId !== parseInt(studentId) || s.courseId !== parseInt(courseId)) return s;
                    return { ...s, examData: { ...s.examData, [`${category}_override`]: res.data.override } };
                }));
                setOverrideDialog({ open: false, step: 1 });
            } catch (err) {
                setConfirmDialog({ open: true, title: 'Error', message: err.response?.data?.error || 'Failed to clear override', onConfirm: null });
                setOverrideDialog(prev => ({ ...prev, saving: false }));
            }
            return;
        }

        setOverrideDialog(prev => ({ ...prev, computing: true }));
        try {
            const body = { student_id: parseInt(studentId), course_id: parseInt(courseId), category, preview: true };
            if (mcq !== undefined) body.mcq_score = mcqVal;
            if (frq !== undefined) body.frq_score = frqVal;
            const res = await Axios.put(
                `${process.env.REACT_APP_URL}/api/v1/test-results/save-score-override`,
                body,
                { headers: { Authorization: getAuthHeader() } }
            );
            const computed = res.data.override || {};
            setOverrideDialog(prev => ({
                ...prev,
                computing: false,
                step: 2,
                computedTotal: computed.total || null,
                computedComposite: computed.composite_score ?? null,
                computedCompositeMax: computed.composite_max ?? null,
                computedAp: computed.scaled_score != null ? String(computed.scaled_score) : '',
            }));
        } catch (err) {
            setConfirmDialog({ open: true, title: 'Error', message: err.response?.data?.error || 'Failed to compute', onConfirm: null });
            setOverrideDialog(prev => ({ ...prev, computing: false }));
        }
    };

    const handleSaveOverride = async () => {
        const { studentId, courseId, category, mcq, frq, computedAp } = overrideDialog;
        const mcqVal = mcq === '' ? null : parseFloat(mcq);
        const frqVal = frq === '' ? null : parseFloat(frq);
        // parseFloat, not parseInt: AMC totals carry a half point (121.5); AP/ACT values are whole
        // numbers and parse identically either way.
        const apVal = computedAp === '' ? null : parseFloat(computedAp);

        setOverrideDialog(prev => ({ ...prev, saving: true }));
        try {
            const body = { student_id: parseInt(studentId), course_id: parseInt(courseId), category };
            body.mcq_score = mcqVal;
            body.frq_score = frqVal;
            if (apVal !== null) body.ap_override = apVal;
            const res = await Axios.put(
                `${process.env.REACT_APP_URL}/api/v1/test-results/save-score-override`,
                body,
                { headers: { Authorization: getAuthHeader() } }
            );
            const newOverride = res.data.override;
            setStudents(prev => prev.map(s => {
                if (s.studentId !== parseInt(studentId) || s.courseId !== parseInt(courseId)) return s;
                return { ...s, examData: { ...s.examData, [`${category}_override`]: newOverride } };
            }));
            setOverrideDialog({ open: false, step: 1 });
        } catch (err) {
            setConfirmDialog({ open: true, title: 'Error', message: err.response?.data?.error || 'Failed to save override', onConfirm: null });
            setOverrideDialog(prev => ({ ...prev, saving: false }));
        }
    };

    const feedbackKey = (studentId, courseId, category) => `${studentId}:${courseId}:${category}`;

    const startEditingFeedback = (studentId, courseId, category, kr, en) => {
        const key = feedbackKey(studentId, courseId, category);
        if (editingFeedback[key]) return; // already editing
        setEditingFeedback(prev => ({ ...prev, [key]: { kr: kr || '', en: en || '' } }));
    };

    const updateEditingFeedback = (key, lang, value) => {
        setEditingFeedback(prev => ({ ...prev, [key]: { ...prev[key], [lang]: value } }));
    };

    const cancelEditingFeedback = (key) => {
        setEditingFeedback(prev => { const next = { ...prev }; delete next[key]; return next; });
    };

    const handleSaveFeedback = async (studentId, courseId, category, key) => {
        const draft = editingFeedback[key];
        if (!draft) return;
        setSavingFeedback(prev => new Set(prev).add(key));
        try {
            await Axios.put(
                `${process.env.REACT_APP_URL}/api/v1/test-results/save-exam-feedback`,
                { student_id: parseInt(studentId), course_id: parseInt(courseId), category, ai_feedback: draft.kr, ai_feedback_en: draft.en },
                { headers: { Authorization: getAuthHeader() } }
            );
            cancelEditingFeedback(key);
            fetchRowFor(studentId, courseId);
        } catch (err) {
            setConfirmDialog({ open: true, title: 'Error', message: err.response?.data?.error || 'Failed to save feedback', onConfirm: null });
        } finally {
            setSavingFeedback(prev => { const next = new Set(prev); next.delete(key); return next; });
        }
    };

    // Live updates via socket — debounced refetch on score/grading/feedback changes.
    // Short debounce (300ms) coalesces back-to-back emits from bulk operations or
    // parallel grading completions without making the UI feel laggy.
    useEffect(() => {
        if (!socket) return;
        // Many server actions emit BOTH global.apTestFRQGraded (legacy, used by
        // other pages) AND global.message with row context. Without dedup, the
        // generic event would trigger a full refetch on top of the targeted
        // row patch. Skip the full refetch when a targeted patch just happened.
        const lastTargetedPatch = { ts: 0 };
        const TARGETED_GUARD_MS = 1500;

        const debouncedRefetch = () => {
            if (Date.now() - lastTargetedPatch.ts < TARGETED_GUARD_MS) return;
            if (refetchTimerRef.current) clearTimeout(refetchTimerRef.current);
            refetchTimerRef.current = setTimeout(() => {
                if (Date.now() - lastTargetedPatch.ts < TARGETED_GUARD_MS) return;
                // Silent: don't unmount the table block (= scroll position survives).
                fetchRef.current?.({ silent: true });
            }, 300);
        };
        socket.on('global.apTestFRQGraded', debouncedRefetch);
        // Skip is_frq_grading_progress — fires per FRQ part (would mean full
        // spreadsheet refetch every couple seconds during a long FRQ grade).
        // While grading is in flight, the cell shows a "Grading…" label driven
        // by scoreData.gradingInFlight (from v2_student_part_scores.grading_status).
        //
        // When the payload identifies a specific (student_id, course_id), do a
        // targeted single-row refetch instead of refetching the whole spreadsheet.
        // Falls back to full refetch when the payload doesn't include row context.
        const msgHandler = (data) => {
            if (!data?.is_test_completion_updated) return;
            if (data.student_id && data.course_id) {
                lastTargetedPatch.ts = Date.now();
                fetchRowRef.current?.(data.student_id, data.course_id);
            } else {
                debouncedRefetch();
            }
        };
        socket.on('global.message', msgHandler);

        // The shared socket is `autoConnect: false` — each page that needs live
        // updates has to connect it explicitly with auth. If the user lands here
        // without first visiting a page that connects (Weekly / APTestProgress),
        // the listeners above never fire. Mirror that same connect-on-mount pattern.
        if (!socket.connected) {
            socket.auth = { token: getAuthToken() };
            socket.connect();
        }

        return () => {
            socket.off('global.apTestFRQGraded', debouncedRefetch);
            socket.off('global.message', msgHandler);
            if (refetchTimerRef.current) clearTimeout(refetchTimerRef.current);
            // Mirror Weekly / APTestProgress: disconnect on unmount so the next
            // page that calls socket.connect() gets a fresh handshake and the
            // server re-emits its bootstrap events (global.users, etc).
            // Without this, navigating TestResults -> Weekly leaves Weekly
            // wedged because its onConnect handler never fires.
            socket.disconnect();
        };
    }, [socket]);

    // Debounced search
    const [searchTimeout, setSearchTimeout] = useState(null);
    const handleSearchChange = (e) => {
        const value = e.target.value;
        if (searchTimeout) clearTimeout(searchTimeout);
        setSearchTimeout(setTimeout(() => {
            setSearchQuery(value);
        }, 500));
    };

    // Export to CSV
    const handleExport = () => {
        if (students.length === 0) return;

        const headers = ['#', '수업코드', '안내번호', '학생명'];
        visibleTests.forEach(test => {
            if (test.hasFrq) {
                headers.push(`${test.title} (Total)`);
                headers.push(`${test.title} (MCQ)`);
                headers.push(`${test.title} (FRQ)`);
            } else {
                headers.push(test.title);
            }
        });

        const rows = students.map((student, idx) => {
            const row = [
                idx + 1,
                student.courseCode || '',
                student.consultationNumber || '',
                student.studentName
            ];
            visibleTests.forEach(test => {
                const scoreData = student.scores[test.id];
                if (!scoreData) {
                    row.push('Not assigned');
                    if (test.hasFrq) { row.push('Not assigned'); row.push('Not assigned'); }
                } else if (scoreData.inProgress) {
                    row.push('In progress');
                    if (test.hasFrq) { row.push('In progress'); row.push('In progress'); }
                } else if (scoreData.gradingInFlight) {
                    row.push('Grading...');
                    if (test.hasFrq) { row.push('Grading...'); row.push('Grading...'); }
                } else if (scoreData.noSubmission) {
                    row.push('No Submission');
                    if (test.hasFrq) { row.push(''); row.push(''); }
                } else if (test.hasFrq) {
                    row.push(`${scoreData.score}/${scoreData.total}`);
                    row.push(`${scoreData.mcqScore ?? ''}/${scoreData.mcqTotal ?? ''}`);
                    row.push(scoreData.frqScore != null ? `${scoreData.frqScore}/${scoreData.frqTotal}` : 'Not graded');
                } else {
                    row.push(`${scoreData.score}/${scoreData.total}`);
                }
            });
            return row;
        });

        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `test_results_${selectedCategory?.name || 'all'}_${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
    };

    // Render score cell
    const scoreChip = (label, value, color) => (
        <Chip
            key={label}
            label={`${label} ${value}`}
            size="small"
            sx={{ height: 19, fontSize: 10.5, bgcolor: color, color: '#fff', fontWeight: 600 }}
        />
    );

    // Format a score value: shows "override(original)/total" if overridden, else "original/total"
    const fmtScore = (raw, overrideVal, total) => {
        if (overrideVal != null) return `${overrideVal}(${raw})/${total}`;
        return `${raw}/${total}`;
    };

    // `scale` is the test's scaled_score scale key (act_1_36 / ap_1_5 / …). This cell shows
    // scaled_score for ANY profiled test, so the chip and the override bounds must follow it.
    const renderScoreCell = (scoreData, examInfo, cellMeta, scale) => {
        const sc = scaleFromKey(scale);
        if (!scoreData) {
            return (
                <Tooltip title="Test isn't assigned to this course" arrow>
                    <Typography variant="body2" color="text.secondary">Not assigned</Typography>
                </Tooltip>
            );
        }
        if (scoreData.noSubmission) {
            return (
                <Tooltip title="Student is assigned but hasn't taken the test yet" arrow>
                    <Typography variant="body2" sx={{ color: colors.redAccent[400] }}>Not started</Typography>
                </Tooltip>
            );
        }
        if (scoreData.inProgress) {
            return (
                <Tooltip title="Session is in progress (student is mid-test or admin reopened)" arrow>
                    <Typography variant="body2" sx={{ color: '#f59e0b' }}>In progress</Typography>
                </Tooltip>
            );
        }
        // Authoritative grading-in-flight check — based on v2_student_part_scores.grading_status
        // on the server. Renders a single "Grading..." label instead of partial chips so the
        // admin doesn't see misleading "0" / partial scores while AI grading is still running.
        if (scoreData.gradingInFlight) {
            return (
                <Tooltip title="AI is grading FRQ parts — scores will appear once finished" arrow>
                    <Typography variant="body2" sx={{ color: '#0288d1', fontStyle: 'italic' }}>Grading…</Typography>
                </Tooltip>
            );
        }

        const { studentId, courseId, category, override } = cellMeta || {};
        const isEditable = !!category;
        const hasOverride = override?.mcq_score != null || override?.frq_score != null;

        const chips = [];

        if (scoreData.hasFrq) {
            const rawMcq = scoreData.mcqScore ?? 0;
            const rawFrq = scoreData.frqScore;

            chips.push(scoreChip('MCQ', fmtScore(rawMcq, override?.mcq_score, scoreData.mcqTotal), '#5c6bc0'));
            // Defensive fallback: gradingInFlight already covers the in-progress case above,
            // so a null frqScore here means the aggregation didn't produce a value (e.g. an
            // edge state we don't expect). Show "—" instead of misleading 0.
            const frqDisplay = rawFrq == null
                ? `—/${scoreData.frqTotal}`
                : fmtScore(rawFrq, override?.frq_score, scoreData.frqTotal);
            chips.push(scoreChip('FRQ', frqDisplay, '#9c27b0'));
            const totalDisplay = rawFrq == null
                ? `—/${scoreData.total}`
                : (hasOverride && override?.total
                    ? `${override.total.split('/')[0]}(${scoreData.score})/${scoreData.total}`
                    : `${scoreData.score}/${scoreData.total}`);
            chips.push(scoreChip('Total', totalDisplay, '#4caf50'));
        } else {
            const rawMcq = scoreData.mcqScore ?? scoreData.score ?? 0;
            chips.push(scoreChip('Total', fmtScore(rawMcq, override?.mcq_score, scoreData.total), '#4caf50'));
        }

        // Composite/AP come from the session-level scoring profile output and apply to any
        // test that has a profile attached, not just midterm/final. Prefer the snapshot in
        // examInfo (post-publish frozen value) when present, otherwise fall back to the live
        // session value on scoreData.
        const rawComp = examInfo?.composite_score ?? scoreData?.compositeScore ?? null;
        const ovComp = override?.composite_score;
        if (rawComp != null || ovComp != null) {
            chips.push(scoreChip('Composite', ovComp != null && rawComp != null ? `${ovComp}(${rawComp})` : (ovComp ?? rawComp), '#607d8b'));
        }
        const rawAp = examInfo?.scaled_score ?? scoreData?.scaledScore ?? null;
        const ovAp = override?.scaled_score;
        if (rawAp != null || ovAp != null) {
            // "102/150" for AMC, bare number for AP / ACT (formatScaled); an override keeps the
            // override(actual) form with the denominator once at the end.
            chips.push(scoreChip(sc.short, ovAp != null && rawAp != null
                ? `${ovAp}(${rawAp})${sc.outOf ? `/${sc.max}` : ''}`
                : formatScaled(sc, ovAp ?? rawAp), '#e65100'));
        }

        const openOverrideDialog = () => {
            setOverrideDialog({
                open: true, step: 1,
                studentId, courseId, category,
                mcq: override?.mcq_score != null ? String(override.mcq_score) : '',
                frq: override?.frq_score != null ? String(override.frq_score) : '',
                rawMcq: scoreData.mcqScore ?? scoreData.score ?? 0,
                rawFrq: scoreData.frqScore ?? 0,
                mcqTotal: scoreData.mcqTotal ?? scoreData.total,
                frqTotal: scoreData.frqTotal ?? 0,
                hasFrq: scoreData.hasFrq && scoreData.frqScore != null,
                scale,
                saving: false, computing: false,
            });
        };

        return (
            <Box display="flex" flexWrap="wrap" gap={0.5} justifyContent="center" alignItems="center">
                {chips}
                {isEditable && scoreData.completed && (
                    <Tooltip title="Override scores" arrow>
                        <IconButton size="small" onClick={openOverrideDialog} sx={{ p: 0.25 }}>
                            <EditIcon sx={{ fontSize: 13 }} />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>
        );
    };

    // Clear AI feedback + admin overrides for one student+course+category.
    // Hoisted to the component scope so the trash button can live at cell level
    // (next to the published-state dot) without duplicating logic.
    const handleClearExamData = ({ studentId, courseId, category, isPublished }) => {
        setClearExamDialog({
            open: true,
            studentId, courseId, category,
            isPublished: !!isPublished,
            alsoUnpublish: true, // default checked when published; ignored when not
        });
    };

    const confirmClearExamData = async () => {
        const { studentId, courseId, category, isPublished, alsoUnpublish } = clearExamDialog;
        setClearExamDialog(prev => ({ ...prev, open: false }));
        try {
            await Axios.delete(
                `${process.env.REACT_APP_URL}/api/v1/test-results/exam-data`,
                {
                    headers: { Authorization: getAuthHeader() },
                    // keep_published is only meaningful when the cell was published
                    // and the admin chose to leave it published.
                    data: { student_id: studentId, course_id: courseId, category, keep_published: isPublished && !alsoUnpublish },
                }
            );
            fetchRowFor(studentId, courseId);
        } catch (err) {
            setConfirmDialog({
                open: true, title: 'Error',
                message: err.response?.data?.error || 'Failed to clear exam data',
                onConfirm: null,
            });
        }
    };

    return (
        <Box sx={{ mx: 3, mt: 2, mb: 1, height: 'calc(100vh - 80px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Header */}
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                <Header title="Test Results" />
                {students.length > 0 && (
                    <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<FileDownloadIcon />}
                        onClick={handleExport}
                        size="small"
                    >
                        Export CSV
                    </Button>
                )}
            </Box>

            {/* Filters */}
            <Box display="flex" gap={2} mb={1} flexWrap="wrap" alignItems="center">
                <Autocomplete
                    multiple
                    disableCloseOnSelect
                    size="small"
                    sx={{ minWidth: 220, maxWidth: 380 }}
                    options={semesters}
                    value={selectedSemesters}
                    onChange={(_, val) => {
                        setSelectedSemesters(val);
                        // Selected courses are scoped to semesters. Switching semester(s)
                        // would otherwise leave stale course chips that filter rows to
                        // nothing or to courses that don't belong to the new cohort.
                        setSelectedCourses([]);
                    }}
                    getOptionLabel={(opt) => opt.label}
                    isOptionEqualToValue={(opt, val) => opt.id === val.id}
                    renderInput={(params) => (
                        <TextField {...params} label={`Semester${selectedSemesters.length > 0 ? ` (${selectedSemesters.length})` : ''}`} />
                    )}
                    renderTags={(value, getTagProps) =>
                        value.map((opt, i) => (
                            <Chip key={opt.id} label={opt.label} size="small" {...getTagProps({ index: i })} />
                        ))
                    }
                />

                <Autocomplete
                    size="small"
                    sx={{ minWidth: 200 }}
                    options={categories}
                    value={selectedCategory}
                    onChange={(_, val) => {
                        setSelectedCategory(val);
                        setSelectedCourses([]);
                    }}
                    getOptionLabel={(opt) => opt.name}
                    isOptionEqualToValue={(opt, val) => opt.id === val.id}
                    filterOptions={(options, { inputValue }) => {
                        if (!inputValue) return options;
                        const lower = inputValue.toLowerCase();
                        const includeIndices = new Set();
                        options.forEach((o, i) => {
                            if (o.name.toLowerCase().includes(lower)) {
                                includeIndices.add(i);
                                // Include parent
                                let targetDepth = o.depth - 1;
                                for (let j = i - 1; j >= 0 && targetDepth >= 0; j--) {
                                    if (options[j].depth === targetDepth) {
                                        includeIndices.add(j);
                                        targetDepth--;
                                    }
                                }
                            }
                        });
                        return options.filter((_, i) => includeIndices.has(i));
                    }}
                    renderOption={(props, opt) => (
                        <li {...props} key={opt.id} style={opt.depth === 0 ? { borderTop: `1px solid ${colors.grey[700]}` } : {}}>
                            <Typography sx={{ whiteSpace: 'pre', fontWeight: opt.depth === 0 ? 700 : 400 }}>
                                {opt.label}
                            </Typography>
                        </li>
                    )}
                    renderInput={(params) => <TextField {...params} label="Folder" />}
                    ListboxProps={{ sx: { maxHeight: 300 } }}
                />

                {/* Always rendered (even when no courses are available for the
                    current filter set) to avoid layout shift on filter changes. */}
                <Autocomplete
                    multiple
                    disableCloseOnSelect
                    size="small"
                    sx={{ minWidth: 220, maxWidth: 480 }}
                    options={courses}
                    value={selectedCourses}
                    onChange={(_, val) => setSelectedCourses(val)}
                    getOptionLabel={(opt) => opt.code || opt.title}
                    isOptionEqualToValue={(opt, val) => opt.id === val.id}
                    noOptionsText="No courses for current filters"
                    renderInput={(params) => (
                        <TextField {...params} label={`Courses${selectedCourses.length > 0 ? ` (${selectedCourses.length})` : ''}`} />
                    )}
                    renderTags={(value, getTagProps) =>
                        value.map((opt, i) => (
                            <Chip key={opt.id} label={opt.code || opt.title} size="small" {...getTagProps({ index: i })} />
                        ))
                    }
                    ListboxProps={{ sx: { maxHeight: 320 } }}
                />

                <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Test Type</InputLabel>
                    <Select
                        value={testCategoryFilter}
                        label="Test Type"
                        onChange={(e) => {
                            setTestCategoryFilter(e.target.value);
                            // Same reasoning as semester/folder: course list is scoped
                            // by the active filters, so stale chips can survive into a
                            // narrower pool that doesn't include them.
                            setSelectedCourses([]);
                        }}
                    >
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="regular">Regular</MenuItem>
                        <MenuItem value="midterm">Midterm</MenuItem>
                        <MenuItem value="final">Final</MenuItem>
                    </Select>
                </FormControl>

                <Box
                    display="flex"
                    backgroundColor={colors.primary[600]}
                    borderRadius="3px"
                    boxShadow={10}
                    minWidth={200}
                >
                    <InputBase
                        // Remounts on Clear Filters (key bump) so the cleared
                        // searchQuery shows in the input.
                        key={searchInputKey}
                        sx={{ ml: 2, flex: 1 }}
                        placeholder="Search student..."
                        // defaultValue (uncontrolled) so the persisted searchQuery
                        // surfaces on mount/refresh — otherwise the input looks
                        // empty while the filter is silently applied.
                        defaultValue={searchQuery}
                        onChange={handleSearchChange}
                    />
                    <IconButton type="button" sx={{ p: 1 }}>
                        <SearchIcon />
                    </IconButton>
                </Box>

                <Chip
                    label={`${students.length} students`}
                    color="primary"
                    variant="outlined"
                    size="small"
                    sx={{ alignSelf: 'center' }}
                />

                <Tooltip title="Reset all filters" arrow>
                    <IconButton size="small" onClick={handleResetFilters} sx={{ p: 0.5 }}>
                        <FilterAltOffIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Tooltip>

                <Tooltip title={viewMode === 'per-course' ? 'Switch to single-table view' : 'Switch to per-course tables'} arrow>
                    <IconButton size="small" onClick={() => setViewMode(viewMode === 'per-course' ? 'single' : 'per-course')} sx={{ p: 0.5 }}>
                        {viewMode === 'per-course'
                            ? <ViewListIcon sx={{ fontSize: 18 }} />
                            : <ViewModuleIcon sx={{ fontSize: 18 }} />}
                    </IconButton>
                </Tooltip>
            </Box>

            {/* Actions */}
            <Divider sx={{ mb: 1 }} />
            <Box display="flex" gap={2} mb={3} flexWrap="wrap" alignItems="center">
                <Button
                    size="small"
                    variant="outlined"
                    disabled={midtermFinalTests.length === 0}
                    startIcon={expanded ? <UnfoldLessIcon /> : <UnfoldMoreIcon />}
                    onClick={() => setExpanded(!expanded)}
                >
                    {expanded ? 'Collapse' : 'Expand'} Feedback
                </Button>

                <Box display="flex" alignItems="center" gap={0.25} sx={{ minWidth: 150 }}>
                    <Checkbox
                        size="small"
                        disabled={midtermFinalTests.length === 0}
                        checked={isAllSelected}
                        indeterminate={selectedCells.size > 0 && !isAllSelected}
                        onChange={toggleSelectAll}
                        sx={{ p: 0 }}
                    />
                    <Typography variant="caption" color="text.secondary" noWrap>
                        {selectedCells.size > 0 ? `${selectedCells.size} selected` : 'Select all midterm/finals'}
                    </Typography>
                </Box>

                <Button
                    size="small"
                    variant="contained"
                    color="secondary"
                    disabled={selectedCells.size === 0}
                    onClick={handleBulkGenerateFeedback}
                >
                    Generate AI Feedback ({selectedCells.size})
                </Button>
                <Button
                    size="small"
                    variant="contained"
                    color="success"
                    disabled={selectedCells.size === 0}
                    onClick={() => handleBulkPublish(false)}
                >
                    Publish ({selectedCells.size})
                </Button>
                <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    disabled={selectedCells.size === 0}
                    onClick={() => handleBulkPublish(true)}
                >
                    Unpublish
                </Button>
            </Box>

            {/* Message when no results */}
            {students.length === 0 && !loading && (
                <Box display="flex" justifyContent="center" p={4}>
                    <Typography variant="body1" color="text.secondary">
                        No results found
                    </Typography>
                </Box>
            )}

            {/* Loading */}
            {loading && (
                <Box display="flex" justifyContent="center" p={4}>
                    <CircularProgress />
                </Box>
            )}

            {/* Spreadsheet — toggle between per-course (one table per course, no
                "Not assigned" cells) and single (legacy big grid) views.  Both render
                paths share the same iteration; single mode just collapses to one
                synthetic "group" containing every student and every test. */}
            {students.length > 0 && !loading && (() => {
                let courseGroups;
                if (viewMode === 'per-course') {
                    // Use a Map keyed by courseId to group, so non-contiguous rows
                    // (e.g. when multiple courses share a courseCode after a
                    // multi-semester filter) don't produce duplicate groups with
                    // colliding React keys. Insertion order is preserved, so the
                    // server's sort by (courseCode, courseId) still drives display order.
                    const groupMap = new Map();
                    for (const s of students) {
                        if (!groupMap.has(s.courseId)) {
                            groupMap.set(s.courseId, {
                                courseId: s.courseId,
                                courseCode: s.courseCode,
                                courseTitle: s.courseTitle,
                                courseUuid: s.courseUuid,
                                students: [],
                                isSingleGroup: false,
                            });
                        }
                        groupMap.get(s.courseId).students.push(s);
                    }
                    courseGroups = [...groupMap.values()];
                    // Per-course tables only show columns for tests this course actually has,
                    // sorted by the assignment's due_date ascending (earliest first). Tests
                    // without a due_date sink to the bottom and use title order between them.
                    courseGroups.forEach(g => {
                        g.tests = visibleTests
                            .filter(t => g.students.some(s => s.scores[t.id] != null))
                            .slice()
                            .sort((a, b) => {
                                const aDue = assignedDueDates[`${g.courseId}_${a.rawId}`];
                                const bDue = assignedDueDates[`${g.courseId}_${b.rawId}`];
                                if (aDue && bDue) {
                                    const diff = new Date(aDue) - new Date(bDue);
                                    if (diff !== 0) return diff;
                                    // Tie on due_date → fall through to title (numeric-aware)
                                    // so "Test 2" beats "Test 10" without depending on prior sort.
                                }
                                if (aDue && !bDue) return -1;
                                if (!aDue && bDue) return 1;
                                return (a.title || '').localeCompare(b.title || '', undefined, { numeric: true });
                            });
                    });
                } else {
                    // Single-table mode: one synthetic group with everything.
                    courseGroups = [{
                        courseId: '__all__',
                        students,
                        tests: visibleTests,
                        isSingleGroup: true,
                    }];
                }

                const showCourseColumn = viewMode === 'single';
                // Sticky-left offsets — extra column when course code is shown
                const offsets = showCourseColumn
                    ? { num: 0, code: 40, consult: 120, name: 180 }
                    : { num: 0, consult: 40, name: 100 };

                return (
                    <Box sx={{ flex: 1, overflow: 'auto' }}>
                        {courseGroups.map((group) => (
                            <Box key={group.courseId} sx={{ mb: viewMode === 'per-course' ? 3 : 0 }}>
                                {/* Course header — only in per-course mode. Sits OUTSIDE the
                                    TableContainer so it doesn't get clipped or trail off when
                                    the table scrolls horizontally. Stays at viewport width. */}
                                {!group.isSingleGroup && (
                                    <Box sx={{ px: 1.5, py: 1, bgcolor: 'action.hover', display: 'flex', alignItems: 'center', gap: 1, borderTopLeftRadius: 4, borderTopRightRadius: 4 }}>
                                        <Typography variant="subtitle2" fontWeight="bold">
                                            <span
                                                onClick={() => window.open(`/classrooms/${group.courseUuid}/tests`, '_blank')}
                                                style={{ cursor: 'pointer' }}
                                                onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                                                onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                                            >
                                                {group.courseCode}
                                                <span style={{ marginLeft: 8, fontWeight: 400, color: theme.palette.text.secondary }}>
                                                    {group.courseTitle}
                                                </span>
                                            </span>
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                                            {group.students.length} student{group.students.length !== 1 ? 's' : ''}
                                            {group.tests.length > 0 && ` · ${group.tests.length} test${group.tests.length !== 1 ? 's' : ''}`}
                                        </Typography>
                                    </Box>
                                )}
                                <TableContainer component={Paper} sx={{ borderTopLeftRadius: !group.isSingleGroup ? 0 : undefined, borderTopRightRadius: !group.isSingleGroup ? 0 : undefined }}>
                <Table stickyHeader size="small" sx={{ width: 'auto', '& td, & th': { borderRight: '1px solid', borderRightColor: 'divider' } }}>
                        <TableHead>
                            <TableRow>
                                <TableCell align="center" sx={{ position: 'sticky', left: offsets.num, zIndex: 3, backgroundColor: theme.palette.background.paper, fontWeight: 'bold', width: 40, minWidth: 40, maxWidth: 40 }}>#</TableCell>
                                {showCourseColumn && (
                                    <TableCell align="center" sx={{ position: 'sticky', left: offsets.code, zIndex: 3, backgroundColor: theme.palette.background.paper, fontWeight: 'bold', width: 80, minWidth: 80, maxWidth: 80, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>수업코드</TableCell>
                                )}
                                <TableCell align="center" sx={{ position: 'sticky', left: offsets.consult, zIndex: 3, backgroundColor: theme.palette.background.paper, fontWeight: 'bold', width: 60, minWidth: 60, maxWidth: 60 }}>안내번호</TableCell>
                                <TableCell align="center" sx={{ position: 'sticky', left: offsets.name, zIndex: 3, backgroundColor: theme.palette.background.paper, fontWeight: 'bold', width: 130, minWidth: 130, maxWidth: 130, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>학생명</TableCell>
                                {group.tests.map((test) => {
                                    const isMF = test.test_category === 'midterm' || test.test_category === 'final';
                                    // Fixed column widths so per-course tables align across the page
                                    // and column scroll feels predictable. Long titles ellipsize.
                                    const colWidth = isMF ? 480 : test.hasFrq ? 360 : 240;
                                    return (
                                        <TableCell
                                            key={test.id}
                                            align="center"
                                            sx={{
                                                fontWeight: 'bold',
                                                backgroundColor: theme.palette.background.paper,
                                                ...(isMF && isColumnSelected(test.id, group.students) && {
                                                    backgroundImage: `linear-gradient(${theme.palette.action.selected}, ${theme.palette.action.selected})`,
                                                }),
                                                width: colWidth, minWidth: colWidth, maxWidth: colWidth,
                                                whiteSpace: 'normal',
                                                overflowWrap: 'anywhere',
                                            }}
                                        >
                                            {isMF && (
                                                <Checkbox
                                                    size="small"
                                                    checked={isColumnSelected(test.id, group.students)}
                                                    indeterminate={!isColumnSelected(test.id, group.students) && group.students.some(s => selectedCells.has(cellKey(s.studentId, s.courseId, test.id)))}
                                                    onChange={() => toggleColumn(test.id, group.students)}
                                                    sx={{ p: 0, mr: 0.5 }}
                                                />
                                            )}
                                            {isMF && (
                                                <Chip
                                                    label={test.test_category}
                                                    size="small"
                                                    sx={{
                                                        height: 16, fontSize: 9, mr: 0.5,
                                                        bgcolor: test.test_category === 'midterm' ? 'info.main' : 'warning.main',
                                                        color: '#fff',
                                                    }}
                                                />
                                            )}
                                            <span
                                                onClick={() => window.open(`/admin/tests-v2/${test.rawId}/edit`, '_blank')}
                                                style={{ cursor: 'pointer', overflowWrap: 'anywhere' }}
                                                onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                                                onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                                            >
                                                {test.title}
                                            </span>
                                            <Typography variant="caption" display="block" color="text.secondary">
                                                {(() => {
                                                    // Per-course view: prefix with the admin's per-assignment alias
                                                    // (v2_course_tests.title) when present, and show the assignment's
                                                    // due date. Both are skipped in single view because the same test
                                                    // can have different aliases / due dates across courses.
                                                    const key = `${group.courseId}_${test.rawId}`;
                                                    const assignedTitle = !group.isSingleGroup ? assignedTitles[key] : null;
                                                    const dueIso = !group.isSingleGroup ? assignedDueDates[key] : null;
                                                    const courseTestId = !group.isSingleGroup ? assignedCourseTestIds[key] : null;
                                                    const counts = test.hasFrq
                                                        ? `MCQ ${test.mcqCount} · FRQ ${test.frqMaxPoints}`
                                                        : `${test.mcqCount} Q`;

                                                    let dueNode = null;
                                                    if (dueIso) {
                                                        const due = new Date(dueIso);
                                                        const now = new Date();
                                                        // Date-only past-due: red the day after due_date, never on the due date itself.
                                                        const dueDay = new Date(due.getFullYear(), due.getMonth(), due.getDate());
                                                        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                                                        const isPastDue = today > dueDay;
                                                        const label = `Due ${due.getMonth() + 1}/${due.getDate()}`;
                                                        dueNode = (
                                                            <span style={isPastDue ? { color: '#d32f2f', fontWeight: 600 } : undefined}>
                                                                {label}
                                                            </span>
                                                        );
                                                    }

                                                    // Mirror the test-title pattern: clickable + hover-underline.
                                                    // Opens the per-assignment grading dashboard for this course.
                                                    const titleNode = assignedTitle ? (
                                                        courseTestId ? (
                                                            <span
                                                                onClick={() => window.open(`/classrooms/${group.courseUuid}/test-v2/${courseTestId}/grading`, '_blank')}
                                                                style={{ cursor: 'pointer' }}
                                                                onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                                                                onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                                                            >
                                                                {assignedTitle}
                                                            </span>
                                                        ) : assignedTitle
                                                    ) : null;

                                                    return (
                                                        <>
                                                            {titleNode && <>{titleNode} · </>}
                                                            {dueNode && <>{dueNode} · </>}
                                                            {counts}
                                                        </>
                                                    );
                                                })()}
                                            </Typography>
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {group.students.map((student, idx) => {
                                return (
                                    <TableRow key={`${student.studentId}-${student.courseId}`} hover>
                                        <TableCell align="center" sx={{ position: 'sticky', left: offsets.num, backgroundColor: colors.primary[600], zIndex: 1, width: 40, minWidth: 40, maxWidth: 40 }}>
                                            {idx + 1}
                                        </TableCell>
                                        {showCourseColumn && (
                                            <TableCell align="center" sx={{ position: 'sticky', left: offsets.code, backgroundColor: colors.primary[600], zIndex: 1, width: 80, minWidth: 80, maxWidth: 80, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                <span
                                                    onClick={() => window.open(`/classrooms/${student.courseUuid}/tests`, '_blank')}
                                                    style={{ cursor: 'pointer' }}
                                                    onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                                                    onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                                                >
                                                    {student.courseCode}
                                                </span>
                                            </TableCell>
                                        )}
                                        <TableCell align="center" sx={{ position: 'sticky', left: offsets.consult, backgroundColor: colors.primary[600], zIndex: 1, width: 60, minWidth: 60, maxWidth: 60, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {student.consultationNumber}
                                        </TableCell>
                                        <TableCell align="center" sx={{ position: 'sticky', left: offsets.name, backgroundColor: colors.primary[600], zIndex: 1, width: 130, minWidth: 130, maxWidth: 130, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {student.studentName}
                                        </TableCell>
                                        {group.tests.map((test) => {
                                            const scoreData = student.scores[test.id];
                                            const isMidtermFinal = test.test_category === 'midterm' || test.test_category === 'final';
                                            const rawExamInfo = isMidtermFinal && student.examData
                                                ? student.examData[test.test_category]
                                                : null;
                                            // Only show feedback if this test matches the one that generated the data
                                            const examInfo = rawExamInfo && rawExamInfo.test_id === test.rawId ? rawExamInfo : null;

                                            // Reserve top padding whenever the cell renders the published-state
                                            // dot or the trashcan icon, so they don't overlap the score pills.
                                            // Both icons are absolute-positioned in the cell's top-right corner.
                                            const hasOverlayIcons = isMidtermFinal && (
                                                !!examInfo ||
                                                !!student.examData?.[test.test_category] ||
                                                (student.examData?.[`${test.test_category}_override`] && Object.keys(student.examData[`${test.test_category}_override`]).length > 0)
                                            );
                                            const bodyColWidth = isMidtermFinal ? 480 : test.hasFrq ? 360 : 240;
                                            return (
                                                <TableCell key={test.id} align="center" sx={{
                                                    verticalAlign: 'top',
                                                    position: 'relative',
                                                    pt: (expanded && isMidtermFinal) || hasOverlayIcons ? 3.5 : undefined,
                                                    width: bodyColWidth, minWidth: bodyColWidth, maxWidth: bodyColWidth,
                                                    backgroundColor: isMidtermFinal && selectedCells.has(cellKey(student.studentId, student.courseId, test.id))
                                                        ? 'action.selected' : undefined,
                                                }}>
                                                    {isMidtermFinal && examInfo && (
                                                        <Tooltip title={examInfo.published ? 'Published to parents' : 'Draft — not visible to parents'} arrow placement="top">
                                                            <Box
                                                                sx={{
                                                                    width: 7, height: 7, borderRadius: '50%',
                                                                    bgcolor: examInfo.published ? 'success.main' : 'grey.400',
                                                                    position: 'absolute', top: 4, right: 4,
                                                                }}
                                                            />
                                                        </Tooltip>
                                                    )}
                                                    {isMidtermFinal && (
                                                        student.examData?.[test.test_category] ||
                                                        (student.examData?.[`${test.test_category}_override`] && Object.keys(student.examData[`${test.test_category}_override`]).length > 0)
                                                    ) && (
                                                        <Tooltip title={`Clear ${test.test_category} AI feedback and overrides for this student (session scores kept)`} arrow placement="top">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleClearExamData({
                                                                    studentId: student.studentId,
                                                                    courseId: student.courseId,
                                                                    category: test.test_category,
                                                                    isPublished: !!examInfo?.published,
                                                                })}
                                                                sx={{ position: 'absolute', top: 0, right: 14, p: 0.25 }}
                                                            >
                                                                <DeleteOutlineIcon sx={{ fontSize: 13 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    {expanded && isMidtermFinal && (
                                                        <Box sx={{ position: 'absolute', top: 2, left: 2, display: 'flex', alignItems: 'center', gap: 0.25 }}>
                                                            <Checkbox
                                                                size="small"
                                                                checked={selectedCells.has(cellKey(student.studentId, student.courseId, test.id))}
                                                                onChange={() => toggleCell(student.studentId, student.courseId, test.id)}
                                                                sx={{ p: 0, '& .MuiSvgIcon-root': { fontSize: 16 } }}
                                                            />
                                                            {examInfo?.published ? (
                                                                <Chip label="Published" size="small" color="success" sx={{ height: 14, fontSize: 8 }} />
                                                            ) : examInfo?.ai_feedback ? (
                                                                <Chip label="Draft" size="small" variant="outlined" sx={{ height: 14, fontSize: 8 }} />
                                                            ) : scoreData?.completed ? (
                                                                <Chip label="No feedback" size="small" variant="outlined" sx={{ height: 14, fontSize: 8, borderStyle: 'dashed' }} />
                                                            ) : null}
                                                        </Box>
                                                    )}
                                                    {renderScoreCell(scoreData, examInfo, isMidtermFinal ? {
                                                        studentId: student.studentId,
                                                        courseId: student.courseId,
                                                        category: test.test_category,
                                                        override: student.examData?.[`${test.test_category}_override`] || null,
                                                    } : null, test.scale)}
                                                    {/* Expanded: AI feedback for midterm/final */}
                                                    {expanded && isMidtermFinal && (
                                                        <Box sx={{ mt: 0.5 }}>
                                                            {generatingCells.has(feedbackKey(student.studentId, student.courseId, test.test_category)) && (
                                                                <Box display="flex" alignItems="center" justifyContent="center" py={1} sx={{ borderRadius: 1, bgcolor: 'action.hover' }}>
                                                                    <CircularProgress size={14} />
                                                                    <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5, fontSize: 10 }}>Generating...</Typography>
                                                                </Box>
                                                            )}
                                                            {!generatingCells.has(feedbackKey(student.studentId, student.courseId, test.test_category)) && (examInfo?.ai_feedback || examInfo?.ai_feedback_en) && (() => {
                                                                const fbKey = feedbackKey(student.studentId, student.courseId, test.test_category);
                                                                const draft = editingFeedback[fbKey];
                                                                const saving = savingFeedback.has(fbKey);
                                                                const regen = regenPreview[fbKey];
                                                                return (
                                                                    <Box sx={{ borderRadius: 1, bgcolor: 'action.hover', textAlign: 'left', p: 1 }}>
                                                                        {/* Regenerate button */}
                                                                        {!draft && !regen && (
                                                                            <Box display="flex" justifyContent="flex-end" mb={0.5}>
                                                                                <Tooltip title="Regenerate AI feedback" arrow>
                                                                                    <IconButton
                                                                                        size="small"
                                                                                        onClick={() => handleRegenPreview(student.studentId, student.courseId, test.test_category)}
                                                                                        sx={{ p: 0.25 }}
                                                                                    >
                                                                                        <AutorenewIcon sx={{ fontSize: 14 }} />
                                                                                    </IconButton>
                                                                                </Tooltip>
                                                                            </Box>
                                                                        )}

                                                                        {/* Regen preview: current + new comparison */}
                                                                        {regen ? (
                                                                            <Box>
                                                                                <Box display="flex" flexDirection="column" gap={0.5}>
                                                                                    <Box sx={{ p: 0.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                                                                                        <Typography variant="caption" fontWeight={700} fontSize={9} color="text.secondary">CURRENT</Typography>
                                                                                        <Typography variant="caption" fontSize={10} color="text.secondary" display="block" mb={0.25}>KR</Typography>
                                                                                        <Typography variant="caption" sx={{ fontSize: 10, lineHeight: 1.3, display: 'block', whiteSpace: 'pre-wrap', mb: 0.5 }}>
                                                                                            {examInfo.ai_feedback || '—'}
                                                                                        </Typography>
                                                                                        <Typography variant="caption" fontSize={10} color="text.secondary" display="block" mb={0.25}>EN</Typography>
                                                                                        <Typography variant="caption" sx={{ fontSize: 10, lineHeight: 1.3, display: 'block', whiteSpace: 'pre-wrap' }}>
                                                                                            {examInfo.ai_feedback_en || '—'}
                                                                                        </Typography>
                                                                                    </Box>
                                                                                    <Box sx={{ p: 0.5, border: '1px dashed', borderColor: 'info.main', borderRadius: 1 }}>
                                                                                        <Typography variant="caption" fontWeight={700} fontSize={9} color="info.main">NEW</Typography>
                                                                                        {regen.loading ? (
                                                                                            <Box display="flex" alignItems="center" py={1} justifyContent="center">
                                                                                                <CircularProgress size={14} />
                                                                                                <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5, fontSize: 10 }}>Generating...</Typography>
                                                                                            </Box>
                                                                                        ) : (
                                                                                            <>
                                                                                                <Typography variant="caption" fontSize={10} color="text.secondary" display="block" mb={0.25}>KR</Typography>
                                                                                                <Typography variant="caption" sx={{ fontSize: 10, lineHeight: 1.3, display: 'block', whiteSpace: 'pre-wrap', mb: 0.5 }}>
                                                                                                    {regen.kr || '—'}
                                                                                                </Typography>
                                                                                                <Typography variant="caption" fontSize={10} color="text.secondary" display="block" mb={0.25}>EN</Typography>
                                                                                                <Typography variant="caption" sx={{ fontSize: 10, lineHeight: 1.3, display: 'block', whiteSpace: 'pre-wrap' }}>
                                                                                                    {regen.en || '—'}
                                                                                                </Typography>
                                                                                            </>
                                                                                        )}
                                                                                    </Box>
                                                                                </Box>
                                                                                {!regen.loading && (
                                                                                    <Box display="flex" gap={0.5} justifyContent="flex-end" mt={0.5}>
                                                                                        <Button size="small" onClick={() => dismissRegenPreview(fbKey)} sx={{ fontSize: 10, minWidth: 0, px: 1 }}>
                                                                                            Keep Current
                                                                                        </Button>
                                                                                        <Button
                                                                                            size="small"
                                                                                            variant="contained"
                                                                                            color="info"
                                                                                            disabled={saving}
                                                                                            onClick={() => acceptRegenPreview(student.studentId, student.courseId, test.test_category)}
                                                                                            sx={{ fontSize: 10, minWidth: 0, px: 1 }}
                                                                                        >
                                                                                            {saving ? 'Saving...' : 'Accept New'}
                                                                                        </Button>
                                                                                    </Box>
                                                                                )}
                                                                            </Box>
                                                                        ) : !draft ? (
                                                                            /* Read mode — click to edit */
                                                                            <Box
                                                                                onClick={() => startEditingFeedback(student.studentId, student.courseId, test.test_category, examInfo.ai_feedback, examInfo.ai_feedback_en)}
                                                                                sx={{ cursor: 'pointer', '&:hover': { opacity: 0.7 } }}
                                                                            >
                                                                                {examInfo.ai_feedback && (
                                                                                    <>
                                                                                        <Typography variant="caption" fontWeight={600} fontSize={10} color="text.secondary">KR</Typography>
                                                                                        <Typography variant="caption" sx={{ fontSize: 11, lineHeight: 1.4, display: 'block', whiteSpace: 'pre-wrap', mb: 0.5 }}>
                                                                                            {examInfo.ai_feedback}
                                                                                        </Typography>
                                                                                    </>
                                                                                )}
                                                                                {examInfo.ai_feedback_en && (
                                                                                    <>
                                                                                        <Typography variant="caption" fontWeight={600} fontSize={10} color="text.secondary">EN</Typography>
                                                                                        <Typography variant="caption" sx={{ fontSize: 11, lineHeight: 1.4, display: 'block', whiteSpace: 'pre-wrap' }}>
                                                                                            {examInfo.ai_feedback_en}
                                                                                        </Typography>
                                                                                    </>
                                                                                )}
                                                                            </Box>
                                                                        ) : (
                                                                            /* Edit mode */
                                                                            <Box>
                                                                                <Typography variant="caption" fontWeight={600} fontSize={10} color="text.secondary">KR</Typography>
                                                                                <InputBase
                                                                                    multiline
                                                                                    minRows={2}
                                                                                    value={draft.kr}
                                                                                    onChange={(e) => updateEditingFeedback(fbKey, 'kr', e.target.value)}
                                                                                    sx={{ fontSize: 11, lineHeight: 1.4, width: '100%', p: 0.5, mb: 0.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
                                                                                />
                                                                                <Typography variant="caption" fontWeight={600} fontSize={10} color="text.secondary">EN</Typography>
                                                                                <InputBase
                                                                                    multiline
                                                                                    minRows={2}
                                                                                    value={draft.en}
                                                                                    onChange={(e) => updateEditingFeedback(fbKey, 'en', e.target.value)}
                                                                                    sx={{ fontSize: 11, lineHeight: 1.4, width: '100%', p: 0.5, mb: 0.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
                                                                                />
                                                                                <Box display="flex" gap={0.5} justifyContent="flex-end">
                                                                                    <Button size="small" onClick={() => cancelEditingFeedback(fbKey)} sx={{ fontSize: 10, minWidth: 0, px: 1 }}>
                                                                                        Cancel
                                                                                    </Button>
                                                                                    <Button
                                                                                        size="small"
                                                                                        variant="contained"
                                                                                        disabled={saving}
                                                                                        onClick={() => handleSaveFeedback(student.studentId, student.courseId, test.test_category, fbKey)}
                                                                                        sx={{ fontSize: 10, minWidth: 0, px: 1 }}
                                                                                    >
                                                                                        {saving ? 'Saving...' : 'Save'}
                                                                                    </Button>
                                                                                </Box>
                                                                            </Box>
                                                                        )}
                                                                    </Box>
                                                                );
                                                            })()}
                                                        </Box>
                                                    )}
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                                </TableContainer>
                            </Box>
                        ))}
                    </Box>
                );
            })()}

            {/* Confirm / info dialog */}
            <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog(prev => ({ ...prev, open: false }))}>
                <DialogTitle>{confirmDialog.title}</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ whiteSpace: 'pre-wrap' }}>{confirmDialog.message}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    {confirmDialog.onConfirm ? (
                        <>
                            <Button onClick={() => setConfirmDialog(prev => ({ ...prev, open: false }))}>Cancel</Button>
                            <Button variant="contained" color="error" onClick={confirmDialog.onConfirm}>Confirm</Button>
                        </>
                    ) : (
                        <Button onClick={() => setConfirmDialog(prev => ({ ...prev, open: false }))}>OK</Button>
                    )}
                </DialogActions>
            </Dialog>

            {/* Clear exam-data dialog (trashcan icon) — feedback + override always cleared;
                publish state cleared only when "Also unpublish" is checked. */}
            <Dialog open={clearExamDialog.open} onClose={() => setClearExamDialog(prev => ({ ...prev, open: false }))}>
                <DialogTitle>Clear exam data</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This will remove AI feedback and any admin score overrides for this student's {clearExamDialog.category}. Test scores from the session are kept — re-running Regrade will repopulate.
                    </DialogContentText>
                    {clearExamDialog.isPublished && (
                        <FormControlLabel
                            sx={{ mt: 1.5 }}
                            control={
                                <Checkbox
                                    checked={clearExamDialog.alsoUnpublish}
                                    onChange={(e) => setClearExamDialog(prev => ({ ...prev, alsoUnpublish: e.target.checked }))}
                                />
                            }
                            label="Also unpublish"
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setClearExamDialog(prev => ({ ...prev, open: false }))}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={confirmClearExamData}>Clear</Button>
                </DialogActions>
            </Dialog>

            {/* Publish dialog with date picker */}
            <Dialog open={publishDialog.open} onClose={() => setPublishDialog(prev => ({ ...prev, open: false }))}>
                <DialogTitle>Publish Grades</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Publishing {publishDialog.items.length} grade(s) to the parent portal. Choose the release date — this determines where it appears in the parent timeline.
                    </DialogContentText>
                    <TextField
                        type="date"
                        label="Release Date"
                        value={publishDialog.publishDate}
                        onChange={(e) => setPublishDialog(prev => ({ ...prev, publishDate: e.target.value }))}
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPublishDialog(prev => ({ ...prev, open: false }))}>Cancel</Button>
                    <Button variant="contained" color="success" onClick={handleConfirmPublish}>Publish</Button>
                </DialogActions>
            </Dialog>

            {/* Score override dialog */}
            <Dialog open={overrideDialog.open} onClose={() => setOverrideDialog({ open: false, step: 1 })} maxWidth="xs" fullWidth>
                <DialogTitle>Override Scores</DialogTitle>
                <DialogContent>
                    {overrideDialog.step === 1 ? (
                        <>
                            <DialogContentText sx={{ mb: 1 }}>
                                Enter override scores for the fields you want to change. Leave a field blank to keep the student's original score for that field. Leave everything blank to clear all overrides.
                            </DialogContentText>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                                Display format: <strong>override(student)/max</strong> — e.g., MCQ 40(20)/60
                            </Typography>
                            <Box display="flex" flexDirection="column" gap={2}>
                                <TextField
                                    type="number"
                                    label={`MCQ (student: ${overrideDialog.rawMcq}/${overrideDialog.mcqTotal})`}
                                    value={overrideDialog.mcq ?? ''}
                                    onChange={(e) => setOverrideDialog(prev => ({ ...prev, mcq: e.target.value }))}
                                    helperText={overrideDialog.mcq !== '' ? `→ MCQ ${overrideDialog.mcq}(${overrideDialog.rawMcq})/${overrideDialog.mcqTotal}` : 'No override'}
                                    fullWidth
                                    autoFocus
                                    inputProps={{ min: 0, step: 1 }}
                                />
                                {overrideDialog.hasFrq && (
                                    <TextField
                                        type="number"
                                        label={`FRQ (student: ${overrideDialog.rawFrq}/${overrideDialog.frqTotal})`}
                                        value={overrideDialog.frq ?? ''}
                                        onChange={(e) => setOverrideDialog(prev => ({ ...prev, frq: e.target.value }))}
                                        helperText={overrideDialog.frq !== '' ? `→ FRQ ${overrideDialog.frq}(${overrideDialog.rawFrq})/${overrideDialog.frqTotal}` : 'No override'}
                                        fullWidth
                                        inputProps={{ min: 0, step: 1 }}
                                    />
                                )}
                            </Box>
                        </>
                    ) : (
                        <>
                            <DialogContentText sx={{ mb: 2 }}>
                                Review computed scores. The scaled score applies only to tests with a scoring profile — leave it empty to skip.
                            </DialogContentText>
                            <Box display="flex" flexDirection="column" gap={1.5}>
                                {overrideDialog.computedTotal && (
                                    <TextField label="Total" value={overrideDialog.computedTotal} fullWidth disabled size="small" />
                                )}
                                {overrideDialog.computedComposite != null && (
                                    <TextField label="Composite" value={overrideDialog.computedComposite} fullWidth disabled size="small" />
                                )}
                                {/* Bounds and label follow the test's scale — hardcoding 1-5 made an
                                    ACT composite impossible to enter. */}
                                <TextField
                                    type="number"
                                    label={`${scaleFromKey(overrideDialog.scale).label} (${scaleFromKey(overrideDialog.scale).min}-${scaleFromKey(overrideDialog.scale).max})`}
                                    value={overrideDialog.computedAp ?? ''}
                                    onChange={(e) => setOverrideDialog(prev => ({ ...prev, computedAp: e.target.value }))}
                                    helperText="Leave empty to skip (tests without a scoring profile). Otherwise auto-computed from the composite — edit if needed."
                                    fullWidth
                                    inputProps={{ min: scaleFromKey(overrideDialog.scale).min, max: scaleFromKey(overrideDialog.scale).max, step: scaleFromKey(overrideDialog.scale).step ?? 1 }}
                                />
                            </Box>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOverrideDialog({ open: false, step: 1 })}>Cancel</Button>
                    {overrideDialog.step === 2 && (
                        <Button onClick={() => setOverrideDialog(prev => ({ ...prev, step: 1 }))}>Back</Button>
                    )}
                    {overrideDialog.step === 1 ? (
                        <Button
                            variant="contained"
                            disabled={overrideDialog.computing}
                            onClick={handleComputeOverride}
                        >
                            {overrideDialog.computing ? 'Computing...' : 'Compute'}
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            disabled={overrideDialog.saving}
                            onClick={handleSaveOverride}
                        >
                            {overrideDialog.saving ? 'Saving...' : 'Save'}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default TestResults;
